"use strict";
import { CurrencyAmount, V2_FACTORY_ADDRESSES } from "@uniswap/sdk-core";
import { Pair, computePairAddress } from "@uniswap/v2-sdk";
import { useMemo } from "react";
import { assume0xAddress } from "utils/wagmi";
import { useReadContracts } from "wagmi";
export var PairState = /* @__PURE__ */ ((PairState2) => {
  PairState2[PairState2["LOADING"] = 0] = "LOADING";
  PairState2[PairState2["NOT_EXISTS"] = 1] = "NOT_EXISTS";
  PairState2[PairState2["EXISTS"] = 2] = "EXISTS";
  PairState2[PairState2["INVALID"] = 3] = "INVALID";
  return PairState2;
})(PairState || {});
export function useV2Pairs(currencies) {
  const chainId = currencies[0]?.[0]?.chainId;
  const tokens = useMemo(
    () => currencies.map(([currencyA, currencyB]) => [currencyA?.wrapped, currencyB?.wrapped]),
    [currencies]
  );
  const pairAddresses = useMemo(
    () => tokens.map(([tokenA, tokenB]) => {
      return tokenA && tokenB && tokenA.chainId === tokenB.chainId && !tokenA.equals(tokenB) && V2_FACTORY_ADDRESSES[tokenA.chainId] ? computePairAddress({ factoryAddress: V2_FACTORY_ADDRESSES[tokenA.chainId], tokenA, tokenB }) : void 0;
    }),
    [tokens]
  );
  const { data, isLoading } = useReadContracts({
    contracts: useMemo(() => {
      return pairAddresses.map(
        (pairAddress) => ({
          address: assume0xAddress(pairAddress) ?? "0x",
          // Edge case: if an address is undefined, we pass in a blank address to keep the result array the same length as pairAddresses
          abi: [
            {
              constant: true,
              inputs: [],
              name: "getReserves",
              outputs: [
                {
                  internalType: "uint112",
                  name: "reserve0",
                  type: "uint112"
                },
                {
                  internalType: "uint112",
                  name: "reserve1",
                  type: "uint112"
                },
                {
                  internalType: "uint32",
                  name: "blockTimestampLast",
                  type: "uint32"
                }
              ],
              payable: false,
              stateMutability: "view",
              type: "function"
            }
          ],
          functionName: "getReserves",
          chainId
        })
      );
    }, [pairAddresses, chainId])
  });
  return useMemo(() => {
    if (isLoading) {
      return Array.from({ length: pairAddresses.length }, () => [0 /* LOADING */, null]);
    }
    return data?.map(({ result }, i) => {
      const tokenA = tokens[i][0];
      const tokenB = tokens[i][1];
      if (!tokenA || !tokenB || tokenA.equals(tokenB)) {
        return [3 /* INVALID */, null];
      }
      if (!result) {
        return [1 /* NOT_EXISTS */, null];
      }
      const [reserve0, reserve1] = result;
      const [token0, token1] = tokenA.sortsBefore(tokenB) ? [tokenA, tokenB] : [tokenB, tokenA];
      return [
        2 /* EXISTS */,
        new Pair(
          CurrencyAmount.fromRawAmount(token0, reserve0.toString()),
          CurrencyAmount.fromRawAmount(token1, reserve1.toString())
        )
      ];
    }) ?? [];
  }, [data, isLoading, pairAddresses.length, tokens]);
}
export function useV2Pair(tokenA, tokenB) {
  const inputs = useMemo(() => [[tokenA, tokenB]], [tokenA, tokenB]);
  return useV2Pairs(inputs)[0];
}
